import type { State } from "../types/boxes";

/**
 * Single source of truth for state colours. Mirrors the rich-CLI palette
 * (`mttr.__main__._state_color`) and the email template's HTML colours.
 *
 * - UP: green-600           (#16a34a)
 * - SCANNER_ONLY: amber-500 (#f59e0b) — RFID flowing, camera silent
 * - CAMERA_ONLY: yellow-500 (#eab308) — camera flowing, RFID silent (often off-shift)
 * - UNKNOWN: amber-600      (#d97706)
 * - DOWN: red-600           (#dc2626) — covers MQTT-broker-down and CYBERPOWER too;
 *                                       the *cause* lives in `classification`, not state
 * - POWERED_DOWN: red-900   (#7f1d1d)
 *
 * Partial-outage colours — SCANNER_ONLY (amber) and CAMERA_ONLY (yellow) — are
 * deliberately close but distinguishable so an operator can see at a glance
 * which subsystem is broken.
 */
export const STATE_HEX: Record<State, string> = {
  UP: "#16a34a",
  SCANNER_ONLY: "#f59e0b",
  CAMERA_ONLY: "#eab308",
  UNKNOWN: "#d97706",
  DOWN: "#dc2626",
  POWERED_DOWN: "#7f1d1d",
  // UNTOUCHED — neutral gray so operators can tell at a glance these
  // boxes were never onboarded to the v2 telemetry pipeline (cascade
  // sources all None/scu_local) and aren't actively broken.
  UNTOUCHED: "#9ca3af",
};

/** Tailwind text/border classes per state, for chip-style components. */
export const STATE_TW: Record<State, string> = {
  UP: "bg-success-50 text-success-800 border-success-300",
  SCANNER_ONLY: "bg-warning-50 text-warning-700 border-warning-400",
  CAMERA_ONLY: "bg-yellow-50 text-yellow-700 border-yellow-300",
  UNKNOWN: "bg-warning-50 text-warning-800 border-warning-300",
  DOWN: "bg-error-50 text-error-800 border-error-300",
  POWERED_DOWN: "bg-error-100 text-error-900 border-error-500",
  UNTOUCHED: "bg-secondary-50 text-secondary-700 border-secondary-300",
};

/** Tailwind background dot per state, used in the map's pin glyph. */
export const STATE_DOT_TW: Record<State, string> = {
  UP: "bg-success-500",
  SCANNER_ONLY: "bg-warning-400",
  CAMERA_ONLY: "bg-yellow-400",
  UNKNOWN: "bg-warning-500",
  DOWN: "bg-error-500",
  POWERED_DOWN: "bg-error-800",
  UNTOUCHED: "bg-secondary-400",
};

/** Stacked overlay (Push K Slice 5 — Jason 2026-05-21). When a box is
 *  intentionally offline (workcenter jobNo=Stacked OR an MTTR operator
 *  override), the map renders the pin in this darker gray and labels
 *  it STACKED — distinct from the state-based UNTOUCHED so operators
 *  can tell at a glance which signal is firing. Takes precedence over
 *  state color. */
export const STACKED_HEX = "#6b7280"; // tailwind gray-500
export const STACKED_TW = "bg-secondary-200 text-secondary-800 border-secondary-400";
